/**
 * @fileoverview useAuthUser custom hook for user authentication and budget selection.
 *
 * This hook manages the authenticated user's state and synchronizes Auth0 user data
 * with the backend database. It handles:
 * - Syncing Auth0 user to database on login
 * - Loading user's accessible budgets
 * - Managing budget selection
 * - Profile completion flow
 * - Automatic budget selection persistence
 *
 * @description This is one of the most critical hooks in the application as it:
 * 1. Bridges Auth0 authentication with our database
 * 2. Manages global user state and budget context
 * 3. Handles the initial app loading flow
 *
 * The hook automatically syncs the user when:
 * - User first logs in
 * - Component mounts
 * - Auth state changes
 *
 * @module hooks/useAuthUser
 */

import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Custom hook for managing authenticated user state and budget selection.
 *
 * Syncs Auth0 user with backend database and manages budget access.
 *
 * @returns {Object} User state and control functions
 * @returns {number|null} return.userKey - User's database key
 * @returns {string|null} return.userName - User's full name
 * @returns {Array} return.budgets - Budgets user has access to
 * @returns {Object|null} return.selectedBudget - Currently selected budget
 * @returns {Function} return.setSelectedBudget - Change selected budget
 * @returns {Function} return.refreshUser - Manually refresh user data
 * @returns {boolean} return.isLoading - Whether sync is in progress
 * @returns {boolean} return.hasLoadedOnce - Whether initial sync has completed at least once
 * @returns {Error|null} return.error - Any sync error
 * @returns {boolean} return.needsProfileCompletion - Whether user needs to add name
 *
 * @example
 * const authUser = useAuthUser();
 * if (authUser.isLoading) return <Loading />;
 * if (authUser.needsProfileCompletion) return <ProfileForm />;
 * console.log('Selected:', authUser.selectedBudget.budgetName);
 */
export const useAuthUser = () => {
    const { user, isAuthenticated } = useAuth0();
    const [userKey, setUserKey] = useState(null);
    const [userName, setUserName] = useState(null);
    const [budgets, setBudgets] = useState([]);
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Extracted sync function that can be called manually
    const syncUser = async () => {
        if (!isAuthenticated || !user) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Sync user with backend
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/auth/sync-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    auth0Id: user.sub,
                    email: user.email,
                    name: user.name
                })
            });

            if (!response.ok) {
                throw new Error('Failed to sync user');
            }

            const data = await response.json();
            setUserKey(data.user.userKey);
            setUserName(data.user.fullName);
            setBudgets(data.budgets);
            setNeedsProfileCompletion(data.needsProfileCompletion || false);
            setApprovalStatus(data.approvalStatus || 'approved');
            setIsAdmin(data.isAdmin || false);

            // Auto-select budget from localStorage or first available budget
            const savedBudgetKey = localStorage.getItem('selectedBudgetKey');
            // budgetKey is stored as string in both localStorage and data.budgets
            if (savedBudgetKey && data.budgets.find(b => b.budgetKey === savedBudgetKey)) {
                setSelectedBudget(data.budgets.find(b => b.budgetKey === savedBudgetKey));
            } else if (data.budgets.length > 0) {
                setSelectedBudget(data.budgets[0]);
                localStorage.setItem('selectedBudgetKey', data.budgets[0].budgetKey);
            } else {
                // No budgets available - clear selection and localStorage
                setSelectedBudget(null);
                localStorage.removeItem('selectedBudgetKey');
            }

            setHasLoadedOnce(true);
            setIsLoading(false);
        } catch (err) {
            // Error handling
            setError(err.message);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        syncUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isAuthenticated]);

    // Save selected budget to localStorage when it changes
    useEffect(() => {
        if (selectedBudget) {
            localStorage.setItem('selectedBudgetKey', selectedBudget.budgetKey);
        }
    }, [selectedBudget]);

    const handleBudgetChange = (budgetKey) => {
        // budgetKey comes in as string from dropdown, and budgetKeys in array are strings too
        const budget = budgets.find(b => b.budgetKey === budgetKey);
        if (budget) {
            setSelectedBudget(budget);
        }
    };

    return {
        userKey,
        userName,
        budgets,
        selectedBudget,
        setSelectedBudget: handleBudgetChange,
        refreshUser: syncUser, // Expose syncUser as refreshUser for manual refresh
        isLoading,
        hasLoadedOnce,
        error,
        needsProfileCompletion,
        approvalStatus,
        needsApproval: approvalStatus === 'pending',
        isRejected: approvalStatus === 'rejected',
        isAdmin
    };
};
